/**
 * touch-press.js — a finger's press shows the moment it lands.
 *
 * A mouse gets `:active` on mousedown. A finger does not: Chromium shows a
 * touch press only when the finger lifts from a quick tap (or once a held one
 * passes its show-press delay), so the pressed look every stylesheet gives a
 * control arrived with the click, ~95 ms after the touch on the emulated
 * mid-range phone and ~125 ms after it once painted (measured 25 September
 * 2026 on every tap of the phone seats: tabs, close buttons, the store and
 * Foundry doors). The press IS the acknowledgement, and it must come within
 * 100 ms.
 *
 * So a touch or pen pointerdown marks the pressed chain — the target and every
 * ancestor, exactly the elements `:active` matches — with `data-pressed`, and
 * every `:active` rule in the stylesheets reads `:is(:active, [data-pressed])`
 * (same specificity, so nothing in the cascade moves). A mouse is left to the
 * browser's own `:active`.
 *
 * THE MARK OUTLIVES THE RELEASE BY TWO FRAMES. The browser applies its own
 * `:active` for the tap as the finger lifts; clearing the mark on pointerup
 * would leave a frame between the two with the control unpressed — a blink.
 *
 * INSIDE A SCROLLER A PRESS WAITS A BEAT. A finger landing on a list may be
 * starting a scroll, which is why browsers delay their own press there; a
 * press shown at once would flash under every flick. Within anything that can
 * scroll (the page included) the mark waits PRESS_IN_SCROLLER_MS, still well
 * inside the 100 ms, and never comes if the finger travels past the tap slop
 * or the browser takes the gesture (pointercancel). A press that moves past
 * the slop after it was shown is a drag, and clears as the browser's does.
 *
 * A PRESS WHOSE ANSWER IS STILL COMING STAYS ANSWERED. A tap that opens
 * something slow (a card's enlargement waits for its raster: 112-356 ms after
 * the touch on the phone seats) would lose its pressed look two frames after
 * the finger lifts and show nothing until the answer. `holdPending(element)`
 * marks the element `data-press-pending` once PRESS_ACK_DELAY_MS has passed, so
 * an answer inside that grace shows nothing extra, until the caller releases
 * it. The caller owns when (touch-preview.js: the enlargement shows, or the tap
 * is abandoned); this module owns the mark, and drops it with the window's focus
 * like every other press.
 *
 * Contract: tests/unit/ui/touchPress.test.js; the stylesheet side is
 * tests/unit/styles/touchPressSelectors.test.js.
 */
let TouchPress = {};

// A re-evaluated module (Jest, hot reload) must not leave the previous
// instance's listeners marking alongside its own.
window.__moonfallTouchPressUninstall?.();

TouchPress = (function () {
    const ATTR = 'data-pressed';
    /* Long enough for a flick to declare itself, short enough that the press
       still paints inside the 100 ms a press has to answer in. */
    const PRESS_IN_SCROLLER_MS = 50;
    /* The app's shared tap slop (press-sound.js DRAG_SLOP_PX). */
    const TAP_SLOP_PX = 12;
    const PENDING_ATTR = 'data-press-pending';
    /* An answer that lands within a few frames IS the acknowledgement; only a
       wait long enough to see earns the pending look (collection.js uses the
       same 60 ms for its busy states). Still well inside the 100 ms. */
    const PRESS_ACK_DELAY_MS = 60;

    let marked = [];
    let pending = null; // { pointerId, x, y, chain, timer }
    let shown = null;   // { pointerId, x, y }
    const held = new Set(); // the release of every holdPending still open

    function isTouchLike(event) {
        return event.pointerType === 'touch' || event.pointerType === 'pen';
    }

    function chainOf(target) {
        const chain = [];
        for (let node = target; node && node.nodeType === 1; node = node.parentElement) chain.push(node);
        return chain;
    }

    function canScroll(element) {
        let style;
        try { style = getComputedStyle(element); } catch (_) { return false; }
        const scrollsY = (style.overflowY === 'auto' || style.overflowY === 'scroll')
            && element.scrollHeight > element.clientHeight + 1;
        const scrollsX = (style.overflowX === 'auto' || style.overflowX === 'scroll')
            && element.scrollWidth > element.clientWidth + 1;
        return scrollsY || scrollsX;
    }

    function inScroller(chain) {
        const root = document.scrollingElement || document.documentElement;
        for (const element of chain) {
            if (element === root || element === document.body) continue;
            if (canScroll(element)) return true;
        }
        return Boolean(root) && (root.scrollHeight > root.clientHeight + 1 || root.scrollWidth > root.clientWidth + 1);
    }

    function clearMark() {
        for (const element of marked) element.removeAttribute(ATTR);
        marked = [];
    }

    function mark(chain) {
        clearMark();
        for (const element of chain) {
            if (element.isConnected) element.setAttribute(ATTR, '');
        }
        marked = chain;
    }

    function cancelPending() {
        if (!pending) return;
        clearTimeout(pending.timer);
        pending = null;
    }

    function show(pointerId, x, y, chain) {
        mark(chain);
        shown = { pointerId, x, y };
    }

    function onPointerDown(event) {
        if (!isTouchLike(event) || event.isPrimary === false) return;
        cancelPending();
        shown = null;
        clearMark();
        const target = event.target && event.target.nodeType === 1 ? event.target : null;
        if (!target) return;
        const chain = chainOf(target);
        const { pointerId, clientX: x, clientY: y } = event;
        if (!inScroller(chain)) {
            show(pointerId, x, y, chain);
            return;
        }
        pending = {
            pointerId,
            x,
            y,
            chain,
            timer: setTimeout(() => {
                const press = pending;
                pending = null;
                if (press) show(press.pointerId, press.x, press.y, press.chain);
            }, PRESS_IN_SCROLLER_MS),
        };
    }

    function travelled(from, event) {
        return Math.hypot(event.clientX - from.x, event.clientY - from.y) > TAP_SLOP_PX;
    }

    function onPointerMove(event) {
        if (pending && event.pointerId === pending.pointerId && travelled(pending, event)) cancelPending();
        if (shown && event.pointerId === shown.pointerId && travelled(shown, event)) {
            shown = null;
            clearMark();
        }
    }

    function onPointerUp(event) {
        // Released within the beat: the browser's own :active answers the tap.
        if (pending && event.pointerId === pending.pointerId) cancelPending();
        if (!shown || event.pointerId !== shown.pointerId) return;
        shown = null;
        const released = marked;
        const later = typeof window.requestAnimationFrame === 'function'
            ? (callback) => window.requestAnimationFrame(() => window.requestAnimationFrame(callback))
            : (callback) => setTimeout(callback, 32);
        later(() => {
            if (marked === released) clearMark();
        });
    }

    /**
     * Keep `element` acknowledged while its answer is on the way. Returns the
     * release: idempotent, and it only ever clears its own mark.
     * @param {Element | null | undefined} element
     * @returns {() => void}
     */
    function holdPending(element) {
        if (!element || element.nodeType !== 1) return () => {};
        let shownPending = false;
        const timer = setTimeout(() => {
            if (!held.has(release) || !element.isConnected) return;
            element.setAttribute(PENDING_ATTR, '');
            shownPending = true;
        }, PRESS_ACK_DELAY_MS);
        function release() {
            if (!held.delete(release)) return;
            clearTimeout(timer);
            if (shownPending) element.removeAttribute(PENDING_ATTR);
        }
        held.add(release);
        return release;
    }

    function releaseAllPending() {
        [...held].forEach((release) => release());
    }

    function onPointerCancel(event) {
        if (pending && event.pointerId === pending.pointerId) cancelPending();
        if (shown && event.pointerId === shown.pointerId) {
            shown = null;
            clearMark();
        }
    }

    /** @type {Array<[string, (event: any) => void]>} */
    const listeners = [
        ['pointerdown', onPointerDown],
        ['pointermove', onPointerMove],
        ['pointerup', onPointerUp],
        ['pointercancel', onPointerCancel],
    ];
    /** @type {AddEventListenerOptions} */
    const options = { capture: true, passive: true };
    listeners.forEach(([type, listener]) => document.addEventListener(type, listener, options));
    // A press the page never sees end (the tab loses focus mid-touch) must
    // not stay pressed — the same release the deck piles' press uses.
    function onBlur() {
        cancelPending();
        shown = null;
        clearMark();
        releaseAllPending();
    }
    window.addEventListener('blur', onBlur, { passive: true });

    function uninstall() {
        listeners.forEach(([type, listener]) => document.removeEventListener(type, listener, options));
        window.removeEventListener('blur', onBlur);
        cancelPending();
        shown = null;
        clearMark();
        releaseAllPending();
        if (window.__moonfallTouchPressUninstall === uninstall) delete window.__moonfallTouchPressUninstall;
    }
    window.__moonfallTouchPressUninstall = uninstall;

    return {
        ATTR,
        PENDING_ATTR,
        PRESS_ACK_DELAY_MS,
        PRESS_IN_SCROLLER_MS,
        TAP_SLOP_PX,
        holdPending,
        uninstall,
    };
})();

export { TouchPress };
